import { BaseRouteState, AllPolicyRecordings } from '@shared/data-access'
import { flow, types } from 'mobx-state-tree'
import { actions } from '@sr/routes/common'

export const UserDownloadFilesListState = BaseRouteState.named('UserDownloadFilesListState')
  .props({
    downloadFiles: types.maybeNull(types.array(types.reference(AllPolicyRecordings))),
    pageSize: 15,
    page: 0,
    total: 0,
    search: '',
    searchInput: '', // Input del usuario (sin debounce)
    sourceFilter: 'all', // Por defecto buscar en todas las fuentes
    hasSearched: false, // Flag para saber si ya se hizo una búsqueda
    isSearching: false, // Flag para mostrar estado de búsqueda
    sort: types.maybeNull(
      types.array(
        types.frozen({
          id: 'request_date',
          desc: true,
        }),
      ),
    ),
    filters: types.maybeNull(types.frozen({})),
  })
  .volatile(() => ({
    searchTimeout: null, // Para el debouncing
  }))
  .views((self) => ({
    get pagination() {
      return {
        pageSize: self.pageSize,
        pageIndex: self.page,
      }
    },
  }))
  .actions((self) => ({
    afterAttach() {
      // No cargar datos automáticamente
      // Solo cuando el usuario busque
    },

    beforeDetach() {
      // Limpiar timeout al desmontar
      if (self.searchTimeout) {
        clearTimeout(self.searchTimeout)
      }
    },

    loadDependencies: flow(function* loadDependencies() {
      // Solo buscar si hay un término de búsqueda con al menos 3 caracteres
      const trimmedSearch = self.search?.trim() || ''

      if (!trimmedSearch || trimmedSearch.length < 3) {
        if (trimmedSearch.length === 0) {
          self.downloadFiles = []
          self.total = 0
          self.hasSearched = false
        }
        return
      }

      self.setLoading('UserDownloadFilesListState')
      self.isSearching = true

      try {
        const [property] = self.sort || []

        // Si sourceFilter es 'all', no enviar el filtro de source
        const sourceParam = self.sourceFilter === 'all' ? undefined : self.sourceFilter

        self.downloadFiles = yield self.loadPaginatedAndFilteredDownloadFiles({
          isClear: true,
          sort: property && `${property.desc ? '-' : ''}${property.id}`,
          filters: { ...self.filters, source: sourceParam },
        })

        self.hasSearched = true
      } catch (error) {
        console.error('Error loading videos:', error)
      } finally {
        self.removeLoading('UserDownloadFilesListState')
        self.isSearching = false
      }
    }),

    setSearchInput(input) {
      // Actualizar el input inmediatamente (para que el usuario vea lo que escribe)
      self.searchInput = input

      // Limpiar timeout anterior
      if (self.searchTimeout) {
        clearTimeout(self.searchTimeout)
      }

      // Si el input está vacío, limpiar inmediatamente
      if (!input || input.trim().length === 0) {
        self.search = ''
        self.downloadFiles = []
        self.total = 0
        self.hasSearched = false
        return
      }

      // Debounce: esperar 500ms después de que el usuario deja de escribir
      self.searchTimeout = setTimeout(() => {
        self.setSearch(input)
      }, 500)
    },

    setSearch: flow(function* setSearch(search) {
      const trimmedSearch = search?.trim() || ''

      if (self.search !== trimmedSearch) {
        self.search = trimmedSearch
        self.page = 0

        // Solo buscar si tiene al menos 3 caracteres
        if (trimmedSearch.length >= 3) {
          yield self.loadDependencies()
        } else if (trimmedSearch.length === 0) {
          // Limpiar resultados si se borra el search
          self.downloadFiles = []
          self.total = 0
          self.hasSearched = false
        }
      }
    }),
   
    setSourceFilter: flow(function* setSourceFilter(source) {
      if (self.sourceFilter !== source) {
        self.sourceFilter = source
        self.page = 0
        yield self.loadDependencies()
      }
    }),
    setSourceFilter: flow(function* setSourceFilter(source) {
      if (self.sourceFilter !== source) {
        self.sourceFilter = source
        self.page = 0
        yield self.loadDependencies()
      }
    }),
    onPaginationChange: flow(function* onPaginationChange({
      pageSize,
      pageIndex,
    }) {
      if (pageSize !== self.pageSize || pageIndex !== self.page) {
        self.pageSize = pageSize
        self.page = pageIndex
        yield self.loadDependencies()
      }
    }),
    onSortingChange: flow(function* onSortingChange(sort) {
      self.sort = sort
      yield self.loadDependencies()
    }),
    onFiltersChange: flow(function* onSortingChange(filters) {
      self.filters = filters
      yield self.loadDependencies()
    }),
    ...actions(self),
  }))
